<template>
  <div>
    <v-card class="is-fullheight flex flex-column white-common-box">
      <div class="pa-5 flex-grow-1 flex flex-column">
        <div class="title-font mainColor--text box-title-label">
          Buy credits
        </div>
        <div class="description mt-3">
          Additional credits can be purchased to unlock features in bundles of 10, 25, 50, 100, 500 & 1000. These add-on credits do not expire, even if you cancel your monthly subscription.
        </div>

        <div v-if="activePlans.length > 0" class="mt-4">
          <div v-for="plan in activePlans" :key="plan.id"
            class="active-refill-panel"
            :class="{ 'is-failed': plan.status === 'payment_failed' }">
            <div class="refill-info">
              <div class="refill-title">
                <span v-if="plan.status === 'payment_failed'">Auto-refill payment failed</span>
                <span v-else>Auto-refill active</span>:
                {{ plan.credits }} POP Credits {{ freqLabel[plan.intervalMonths] }}
              </div>
              <div class="refill-detail">
                ${{ plan.chargeAmount.toFixed(2) }}/charge<span v-if="plan.discountPercent > 0"> ({{ plan.discountPercent }}% off)</span>
                <span v-if="plan.nextChargeDate"> · Next charge {{ formatRefillDate(plan.nextChargeDate) }}</span>
              </div>
              <div v-if="plan.status === 'payment_failed'" class="refill-detail">
                Last charge failed. We'll retry automatically. Update your card under Billing &amp; payments → Credit cards.
              </div>
              <div v-if="plan.refills && plan.refills.length > 0" class="refill-history-toggle"
                @click="togglePlanHistory(plan.id)">
                Past refills ({{ plan.refills.length }})
                <i class="material-icons">{{ isPlanHistoryOpen(plan.id) ? 'expand_less' : 'expand_more' }}</i>
              </div>
              <div v-if="isPlanHistoryOpen(plan.id)" class="refill-history">
                <div v-for="refill in plan.refills" :key="refill.id" class="refill-history-item">
                  <span class="history-date">{{ formatRefillDate(refill.paidAt || refill.cycleDate || refill.createdAt) }}</span>
                  <span>{{ refill.credits }} credits</span>
                  <span>${{ refill.chargeAmount.toFixed(2) }}</span>
                  <span class="history-status" :class="'is-' + refill.status">{{ refill.status }}</span>
                </div>
              </div>
            </div>
            <div class="refill-actions">
              <div class="refill-cadence">
                <span v-for="months in [1, 2, 3]" :key="months"
                  class="cadence-pill"
                  :class="{ active: plan.intervalMonths === months }"
                  @click="() => changePlanCadence(plan, months)">
                  {{ months }}mo
                </span>
              </div>
              <v-btn small rounded outlined color="mainColor" class="text-none"
                @click.stop="() => { planPendingCancel = plan; }">
                Cancel
              </v-btn>
            </div>
          </div>
        </div>

        <div v-if="isLoadingPackages" class="mt-2 greenColor--text">Loading available packages..</div>
        <div class="flex-grow-1">
          <v-radio-group v-model="selectedPackage">
            <div v-for="item in packagesToRender" :key="item.value"
              class="package-item"
              :class="{ 'is-selected': item.value == selectedPackage }"
              @click="() => {
                selectedPackage = item.value;
              }">
              <span>
                <v-radio :value="item.value"
                  :selected="item.value == selectedPackage"
                  color="profileAnchorColor"></v-radio>
              </span>
              <div class="flex-grow-1">
                <div class="text">{{ item.text }}</div>
                <div class="explain">
                  The price of one POP Credit is ${{ item.pricePerUnit.toFixed(2) }}
                </div>
              </div>
              <div class="price">
                ${{ calculatePriceForPackage(item.value).toFixed(2) }}
              </div>
              <div v-if="item.saveMoney > 0" class="save-money">
                Save ${{ item.saveMoney.toFixed(2) }}                
              </div>
            </div>
          </v-radio-group>
        </div>
        <div class="mt-3">
          <v-row>
            <v-col cols="8">
              <div class="d-flex align-center">
                <v-text-field outlined dense
                  hide-details
                  color="greenColor"
                  v-model="promoCode"
                  placeholder="Input a coupon code here if you have one"></v-text-field>
                <v-btn
                  color="profileAnchorColor"
                  :disabled="!promoCode"
                  class="ml-3 text-none mainColor--text"
                  @click="applyCoupon">
                  <span>Apply</span>
                </v-btn>
              </div>
              <div class="mt-1">
                <div v-if="appliedCoupon" class="greenColor--text">Coupon applied for {{ appliedCoupon.amountOff }}% discount.</div>
                <div v-else-if="couponError" class="redColor--text" v-html="couponError"></div>
              </div>
            </v-col>
            <v-col cols="4"></v-col>
          </v-row>
        </div>
        <div class="mt-5 d-flex gap10">
          <!-- Lookalike PayPal trigger: opens the checkout popup first; the
               real PayPal iframe button is rendered inside the popup. -->
          <button class="paypal-trigger-button" :disabled="!selectedPackage" @click.stop="submitForPaypal">
            <span class="pp"><span class="pal-a">Pay</span><span class="pal-b">Pal</span></span>
            <span class="pp-sub">The safer, easier way to pay</span>
          </button>
          <v-btn
            color="profileAnchorColor"
            class="button-with-icon text-none whiteColor--text"
            @click.stop="submit"
            :disabled="!selectedPackage">
            <span class="icon">
              <i class="fa fa-money"></i>
            </span>
            <span>Pay with credit card</span>
          </v-btn>
        </div>

        <checkout-confirm-modal
          v-model="showConfirmModal"
          :credits="selectedCredits"
          :base-price="prices[selectedPackage] || 0"
          :one-time-price="purchase"
          :payment-method="paymentMethod"
          :discount-percent="autoRefillDiscount"
          :has-payment-source="hasPaymentSource"
          :error-msg="checkoutError"
          @confirm-one-time="confirmedGoProcess"
          @confirm-auto-refill="startAutoRefill"
          @switch-to-card="paymentMethod = 'stripe'"
          @add-card="goToAddCard">
        </checkout-confirm-modal>

        <div class="modal" :class="{ 'is-active': !!planPendingCancel }">
          <div class="modal-background" @click.stop="planPendingCancel = null"></div>
          <div class="modal-content">
            <div class="box modal-container">
              <div class="subtitle-font mainColor--text">
                Cancel auto-refill? <br />
                Your credits never expire; future charges stop.
              </div>
              <div class="text-right mt-3">
                <v-btn rounded outlined color="mainColor" class="button-with-sicon mainColor--text text-none"
                  @click.stop="planPendingCancel = null">
                  Keep auto-refill
                </v-btn>
                <v-btn rounded color="profileAnchorColor" class="button-with-sicon whiteColor--text text-none ml-2"
                  @click.stop="confirmCancelPlan">
                  Cancel auto-refill
                </v-btn>
              </div>
            </div>
            <button class="custom-modal-close-btn" aria-label="close" @click="() => (planPendingCancel = null)">
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>

        <div class="modal" :class="{ 'is-active': showErrorModal }">
          <div class="modal-background" @click.stop="showErrorModal = false"></div>
          <div class="modal-content">
            <div class="box modal-container">
              <p class="title mainColor--text">Puchase Failure</p>
              <p class="subtitle-font mainColor--text">
                Transaction failure. Please verify your payment method is valid. <br />
                Please remove your card in 'Manage Credit cards' and try again.
              </p>
              <v-btn rounded color="profileAnchorColor" class="button-with-icon mainColor--text text-none"
                @click.stop="showErrorModal = false">Back to Purchases</v-btn>
            </div>
          </div>
          <button class="modal-close is-large" aria-label="close" @click.stop="showErrorModal = false"></button>
        </div>
      </div>
    </v-card>
    <div class="mt-10"></div>
    <v-card class="is-fullheight flex flex-column white-common-box mb-10">
      <div class="pa-5 flex-grow-1 flex flex-column">
        <div class="title-font mainColor--text box-title-label d-flex align-center gap10">
          Here’s how you can exchange POP credits
          <span class="material-icons video-tutorial-icon video-tutorial-icon-size"
            @click="() => {
              $store.commit('setCurrentVideoTutorialToDisplay', {
                title: 'POP Credits Explained',
                videoLink: 'https://www.youtube.com/embed/MX2hpmnTA8Y?si=IJ4L-pLVyjn28wnW',
                description: 'POP Credits are like currency inside your POP account and can be spent how you like on various extra features. In this video, you\'ll learn more about what POP Credits are, how to spend them, and where to purchase them.',
                time: '2m 18s'
              });
            }">
            videocam
          </span>
        </div>
        <div class="my-5">
          <div class="title-font mainColor--text">
            POP AI Writer
          </div>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              1,000 words of new & original optimized Content <br/>
              <small>(includes creating a new Title, H1, Subheadings, and paragraph content)</small>
            </v-col>
            <v-col cols="12" md="3">
              {{popCreditCharges.aiWriterCreditsPer1000Words}} POP credit{{ areCreditsMoreThanOne(popCreditCharges.aiWriterCreditsPer1000Words) ? 's' : '' }}
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              Regenerate title section and/or H1
            </v-col>
            <v-col cols="12" md="3">
              1 POP credit
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              Regenerate list of optimized sub-headings
            </v-col>
            <v-col cols="12" md="3">
              1 POP credit
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              Regenerate new or optimize current paragraph text (by subtitles)
            </v-col>
            <v-col cols="12" md="3">
              1 POP credit
            </v-col>
          </v-row>
        </div>
        <div >
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              <div class="title-font mainColor--text">
                POP run *
              </div>
            </v-col>
            <v-col cols="12" md="3">
              2 POP credits
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              <div class="title-font mainColor--text">
                POP re-run *
              </div>
            </v-col>
            <v-col cols="12" md="3">
              1 POP credit (Update Optimization Score) /<br/>
              2 POP credits (setup changes)
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              <div class="title-font mainColor--text">
                POP Watchdog run *
              </div>
            </v-col>
            <v-col cols="12" md="3">
              1 POP credit
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              <div class="title-font mainColor--text">
                Google NLP run
              </div>
            </v-col>
            <v-col cols="12" md="3">
              {{popCreditCharges.googleNlpCredits}} POP credit{{ areCreditsMoreThanOne(popCreditCharges.googleNlpCredits) ? 's' : '' }}
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="12" md="9">
              <div class="title-font mainColor--text">
                EEAT run
              </div>
            </v-col>
            <v-col cols="12" md="3">
              {{popCreditCharges.eeatCredits}} POP credit{{ areCreditsMoreThanOne(popCreditCharges.eeatCredits) ? 's' : '' }}
            </v-col>
          </v-row>
        </div>
        <div class="mt-4">
          <div class="mainColor--text">
            <small>* POP reports are unlimited for Unlimited and Teams packages</small>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import moment from 'moment'
import { getPureServicePkgName } from '@/utils'
import CheckoutConfirmModal from '@/components/CheckoutConfirmModal.vue'

export default {
  components: { CheckoutConfirmModal },
  data() {
    return {
      isLoadingPackages: false,
      packages: [],
      prices: {},
      packageExplainObj: {
        '10_CREDITS': {
          text: '10 POP Credits',
          pricePerUnit: 1.5,
          saveMoney: 0,
        },
        '25_CREDITS': {
          text: '25 POP Credits',
          pricePerUnit: 1.3,
          saveMoney: 5,
        },
        '50_CREDITS': {
          text: '50 POP Credits',
          pricePerUnit: 1.2,
          saveMoney: 15,
        },
        '100_CREDITS': {
          text: '100 POP Credits',
          pricePerUnit: 1.0,
          saveMoney: 50,
        },
        '500_CREDITS': {
          text: '500 POP Credits',
          pricePerUnit: 0.9,
          saveMoney: 300,
        },
        '1000_CREDITS': {
          text: '1000 POP Credits',
          pricePerUnit: 0.8,
          saveMoney: 700,
        }
      },
      pakcagesPricePerUnit: {
        '10_CREDITS': 1.5,
        '25_CREDITS': 1.3,
        '50_CREDITS': 1.2,
        '100_CREDITS': 1.0,
        '500_CREDITS': 0.9,
        '1000_CREDITS': 0.8,
      },
      selectedPackage: '',
      promoCode: '',
      appliedCoupon: null,
      couponError: '',

      showErrorModal: false,

      paymentMethod: '', // stripe / paypal
      showConfirmModal: false,
      checkoutError: '',
      paypalButtonRendered: false,

      activePlans: [],
      planPendingCancel: null,
      openPlanHistoryIds: [],
      // Tri-state: null until /users/hasPaymentSource/ answers. Kept local
      // (not on the store user object) because setUser commits replace that
      // object and Vue 2 can't react to the property being re-added.
      hasPaymentSource: null,
      freqLabel: { 1: 'every month', 2: 'every 2 months', 3: 'every 3 months' },
    }
  },
  methods: {
    applyCoupon () {
      this.appliedCoupon = null;
      this.couponError = '';
      this.$store.commit('showLoading')
      return this.$store.dispatch('fetchValidatedCoupons', { promoCode: this.promoCode.trim(), isRegisterPage: 0 })
        .then((response) => {
          if (!response.data) {
            this.couponError = 'Invalid coupon';
            return;
          }
          if (response.data && response.data.status == 'SUCCESS') {
            let allServiceCoupons = response.data.coupons
            if (allServiceCoupons.length == 1) {
              const coupon = allServiceCoupons[0];
              if (coupon.couponType == 'Credit Percentage Off') {
                console.log(coupon)
                this.appliedCoupon = coupon;
              } else {
                console.log('no no', coupon)
                this.couponError = 'This coupon is not for credit packages.'
                if (coupon.couponType == 'Credits') {
                  this.couponError += '<br/> Click the Promo code tab and insert the promo code there.'
                }
              }
            } else {
              this.couponError = 'Invalid coupon.'
            }
          } else {
            if (response.data.status == 'FAILURE') {
              this.couponError = response.data.errorMsg ? response.data.errorMsg : 'Invalid coupon'
            }
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    calculatePriceForPackage(package_name) {
      let price = this.prices[package_name] || 0
      if (this.appliedCoupon) {
        console.log('dis count is just', this.appliedCoupon.amountOff, price)
        price -= (price * this.appliedCoupon.amountOff / 100)
      }
      return price            
    },
    submit() {
      this.paymentMethod = 'stripe';
      this.checkoutError = '';
      this.showConfirmModal = true;
    },
    submitForPaypal() {
      this.paymentMethod = 'paypal';
      this.checkoutError = '';
      this.showConfirmModal = true;
      this.$nextTick(() => {
        this.renderPaypalButtonIfNeeded();
      });
    },
    confirmedGoProcess() {
      // One-time card purchase: unchanged Stripe Checkout redirect.
      this.showConfirmModal = false;
      this.$store.commit('showLoading');
      this.$store.dispatch('createStripePayment', {
        packageType: 'CREDIT_PURCHASE',
        selectedPackage: this.selectedPackage,
        amount: this.purchase,
        userID: this.user.id,
        promoCode: this.promoCode.trim(),
      })
      .then(function (response) {
        if (response.data && response.data.url) {
          window.open(response.data.url, '_self')
        }
      })
      .finally(() => {
        this.$store.commit('showLoading');
      })
    },
    startAutoRefill({ intervalMonths }) {
      this.checkoutError = '';
      this.$store.commit('showLoading');
      this.$store.dispatch('createCreditAutoRefill', {
        selectedPackage: this.selectedPackage,
        intervalMonths: intervalMonths,
      })
      .then((response) => {
        if (response.data && response.data.status === 'SUCCESS' && response.data.plan) {
          const plan = response.data.plan;
          this.showConfirmModal = false;
          this.activePlans = [plan, ...this.activePlans.filter(p => p.id !== plan.id)];
          this.$notify({
            group: 'info', type: 'success', duration: 5000,
            text: `Auto-refill started: ${plan.credits} credits ${this.freqLabel[plan.intervalMonths]}`,
          });
          this.$store.dispatch('loadUser');
        } else {
          this.checkoutError = response.data?.msg || 'Error starting auto-refill.';
        }
      })
      .catch((error) => {
        this.checkoutError = error?.response?.data?.msg || 'Error starting auto-refill.';
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    goToAddCard() {
      this.$store.dispatch('createStripeCardAdditionLink')
        .then((response) => {
          if (response.data && response.data.url) {
            window.open(response.data.url, '_self');
          }
        })
    },
    loadActivePlans() {
      this.$store.dispatch('listCreditAutoRefills')
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.activePlans = response.data.plans || [];
          }
        })
        .catch(() => {})
    },
    changePlanCadence(plan, months) {
      if (plan.intervalMonths === months) {
        return;
      }
      this.$store.commit('showLoading');
      this.$store.dispatch('updateCreditAutoRefill', { refillId: plan.id, intervalMonths: months })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS' && response.data.plan) {
            this.activePlans = this.activePlans.map(
              p => (p.id === plan.id ? response.data.plan : p));
            this.$notify({ group: 'info', type: 'success', duration: 5000, text: 'Auto-refill cadence updated' });
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading');
        })
    },
    confirmCancelPlan() {
      const plan = this.planPendingCancel;
      this.planPendingCancel = null;
      if (!plan) {
        return;
      }
      this.$store.commit('showLoading');
      this.$store.dispatch('cancelCreditAutoRefill', { refillId: plan.id })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.activePlans = this.activePlans.filter(p => p.id !== plan.id);
            this.$notify({ group: 'info', type: 'success', duration: 5000, text: 'Auto-refill cancelled' });
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading');
        })
    },
    togglePlanHistory(planId) {
      if (this.openPlanHistoryIds.includes(planId)) {
        this.openPlanHistoryIds = this.openPlanHistoryIds.filter(id => id !== planId);
      } else {
        this.openPlanHistoryIds = [...this.openPlanHistoryIds, planId];
      }
    },
    isPlanHistoryOpen(planId) {
      return this.openPlanHistoryIds.includes(planId);
    },
    formatRefillDate(dateStr) {
      return moment(dateStr).format('MMM D, YYYY');
    },
    renderPaypalButtonIfNeeded() {
      if (this.paypalButtonRendered) {
        return;
      }
      // Rendered on first open of the popup with the PayPal entry point; the
      // container node stays in the DOM afterwards (the modal is CSS-toggled).
      this.paypalButtonRendered = true;
      const self = this
      paypal.Button.render({
        env: PAYPAL_ENV,
        local: 'en_US',
        style: {
          size: 'responsive',
          color: 'gold',
          shape: 'rect',
          height: 36,
          label: 'paypal'
        },
        payment: function (data, actions) {
          if (!self.selectedPackage || self.purchase == 0) {
            return false;
          }

          return self.$store.dispatch('createPayPalPayment', {
            packageType: 'CREDIT_PURCHASE',
            selectedPackage: self.selectedPackage,
            amount: self.purchase,
            userID: self.user.id,
            promoCode: self.promoCode.trim(),
          })
            .then(function (response) {
              return response.data.id
            })
        },
        onAuthorize: function (data, actions) {
          return self.$store.dispatch('buyUniversalCreditsPakcage', {
            description: 'CREDIT_PURCHASE',
            servicePackage: self.selectedPackage,
            amount: self.purchase,
            paymentService: 'paypal',
            paymentID: data.paymentID,
            payerID: data.payerID,
            userID: self.user.id,
            promoCode: self.promoCode.trim(),
          })
          .then(function(response) {
            console.log('credit purchase response', response)
            if (response.data.status === 'SUCCESS') {
              self.showConfirmModal = false
              self.$notify({
                group: 'info', type: 'success', duration: 5000,
                text: `${self.selectedCredits} POP Credits added to your account`,
              })
              self.$store.commit('setUser', response.data.user)
            }
          })
        }
      }, '#modal-paypal-button')
    },
    getPureServicePkgName(arg_serviceName) {
      return getPureServicePkgName(arg_serviceName)
    },
    areCreditsMoreThanOne(credits) {
      return credits > 1;
    }
  },
  computed: {
    popCreditCharges () {
      return this.$store.state.popCreditCharges
    },
    purchase () {
      return this.calculatePriceForPackage(this.selectedPackage)
    },
    user() {
      return this.$store.state.user
    },
    selectedCredits () {
      return parseInt(this.selectedPackage) || 0
    },
    autoRefillDiscount () {
      const discount = this.popCreditCharges ? this.popCreditCharges.autoRefillDiscount : undefined
      return typeof discount === 'number' ? discount : 10
    },
    isPaypalDisabled () {
      if (this.user) {
        return this.user.isPaypalDisabled
      }
      return true
    },
    packagesToRender () {
      return this.packages.map(p => {
        let found = this.packageExplainObj[p]
        return {
          ...found,
          value: p,
        }
      })
    }
  },
  mounted() {

    this.isLoadingPackages = true;
    this.$store.dispatch('getAvailableUniversalPackages')
    .then(response => {
      if (response.data && response.data.status == 'SUCCESS') {
        this.packages = response.data.packages;
        this.prices = response.data.prices;
        this.selectedPackage = this.packages[0];
      }
    })
    .finally(() => {
      this.isLoadingPackages = false;
    })

    this.$store.dispatch('loadUserHasPaymentSources').then(response => {
      if (response.data && response.data.status === 'SUCCESS') {
        this.hasPaymentSource = !!response.data.hasPaymentSource;
        this.$store.commit('setUserHasPaymentSources', response.data.hasPaymentSource);
      }
    }).catch(() => {})
    this.loadActivePlans()
  }
}
</script>

<style lang="scss" scoped>
.description {
  color: var(--v-mainColor-base);
}
.package-item {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  border: 1px solid var(--v-grayColor-base);
  border-radius: 5px;
  margin-top: 10px;
  &.is-selected {
    background: #FFF7F0;
    color: #014751;
    border-color: var(--v-profileAnchorColor-base);
    .text, .explain, .price {
      color: #014751;
    }
  }
  .text {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
  }
  .explain {
    margin-top: 5px;
    font-size: 1rem;
    color: var(--v-mainColor-base);
  }
  .price {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
  }
  .save-money {
    background: #FFF7F0;
    border: 1px solid var(--v-profileAnchorColor-base);
    padding: 5px 10px;
    border-radius: 50vh;
    font-weight: 700;
    color: var(--v-profileAnchorColor-base);
  }
}
.paypal-trigger-button {
  background: #FFC439;
  border: none;
  border-radius: 6px;
  padding: 0 30px;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .pp {
    font-size: 20px;
    font-weight: 800;
    font-style: italic;
    letter-spacing: -.5px;
    .pal-a { color: #003087; }
    .pal-b { color: #009cde; }
  }
  .pp-sub {
    font-size: 10px;
    color: #52461f;
    margin-top: -2px;
  }
}
.active-refill-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  flex-wrap: wrap;
  padding: 15px 18px;
  border: 1.5px solid #25A97A;
  background: #D6FBEE;
  border-radius: 6px;
  margin-bottom: 10px;
  &.is-failed {
    border-color: var(--v-profileAnchorColor-base);
    background: #FFF4E8;
  }
  .refill-title {
    font-weight: 800;
    color: var(--v-mainColor-base);
  }
  .refill-detail {
    font-size: 0.9rem;
    color: var(--v-mainColor-base);
    margin-top: 2px;
  }
  .refill-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .refill-history-toggle {
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
    cursor: pointer;
    user-select: none;
    i.material-icons {
      font-size: 18px;
    }
  }
  .refill-history {
    margin-top: 6px;
    border-top: 1px solid rgba(1, 71, 81, 0.15);
    padding-top: 6px;
  }
  .refill-history-item {
    display: flex;
    gap: 14px;
    align-items: center;
    font-size: 0.85rem;
    color: var(--v-mainColor-base);
    padding: 3px 0;
    .history-date {
      min-width: 100px;
      font-weight: 700;
    }
    .history-status {
      text-transform: capitalize;
      font-weight: 700;
      &.is-paid { color: #25A97A; }
      &.is-pending { color: #316BFF; }
      &.is-failed { color: var(--v-redColor-base, #d9534f); }
      &.is-voided { color: #9AA6A8; }
      &.is-expired { color: #9AA6A8; }
    }
  }
  .refill-cadence {
    display: flex;
    gap: 6px;
  }
  .cadence-pill {
    padding: 4px 12px;
    border: 1.5px solid #25A97A;
    border-radius: 50vh;
    background: #fff;
    color: var(--v-mainColor-base);
    font-weight: 700;
    font-size: 0.85rem;
    cursor: pointer;
    &.active {
      background: #25A97A;
      color: #fff;
    }
  }
}
.purchase_amount_txt {
  color: var(--v-greenColor-base);
  font-weight: bold;
  font-size: x-large !important;
}
</style>